import { KatapultElement, html } from '../../mixins/katapult-element.js';
import { KatapultEditorTemplate } from './katapult-editor-template.js';

import { PortalStyle } from './katapult-model-editor-style.js';

import '../paper-header/paper-header.js';
import '../katapult-elements/katapult-button.js';
import '../katapult-elements/katapult-icon.js';
import { confirmSyncPortalConfig, syncPortalConfigs } from './SyncedPortalConfigHelpers.js';
import { getColumnOptions } from '../pole-application-overview/OverviewColumns.js';

customElements.define(
  'katapult-portal-display-options-editor',
  class extends KatapultEditorTemplate {
    static get template() {
      return html`
        ${super.style} ${PortalStyle}

        <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/attributes" data="{{otherAttributes}}">
        </katapult-firebase-worker>
        <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/config/time_zone" data="{{modelTimeZone}}">
        </katapult-firebase-worker>

        <paper-table class="settings-table">
          <!-- Time Zone -->
          <paper-row>
            <paper-cell icon>
              <material-icon icon="schedule"></material-icon>
            </paper-cell>
            <paper-cell column align-start style="max-width: 300px;">
              <span>Time Zone</span>
              <span style="font-size: 10pt;" italic>Time Zone can be configured in the model under Model Options</span>
            </paper-cell>
            <paper-cell>
              <paper-input disabled no-label-float underline value="[[modelTimeZone]]"></paper-input>
            </paper-cell>
          </paper-row>
          <!-- Capitalize Pole Tags -->
          <paper-row checkbox pointer>
            <paper-cell icon>
              <material-icon icon="title"></material-icon>
            </paper-cell>
            <paper-cell shrink>Capitalize Pole Tags</paper-cell>
            <paper-cell icon>
              <paper-checkbox
                checked="[[_itemData.capitalize_pole_tags]]"
                data-path="_itemData.capitalize_pole_tags"
                on-change="toggleProperty"
              ></paper-checkbox>
            </paper-cell>
          </paper-row>
          <!-- Overview Attributes Sort Attribute -->
          <paper-row input>
            <paper-cell icon>
              <material-icon icon="sort"></material-icon>
            </paper-cell>
            <paper-cell shrink>Overview Attributes Default Sort Attribute</paper-cell>
            <paper-cell>
              <katapult-drop-down
                label="Attribute"
                value="[[item.attribute]]"
                value-path="$key"
                label-path="$key"
                label-function="[[camelCase]]"
                items="[[otherAttributes]]"
                no-label-float
                data-path="_itemData.default_sort_attribute"
                on-selected-changed="updateProperty"
              >
              </katapult-drop-down>
            </paper-cell>
          </paper-row>
          <!-- Overview Attributes -->
          <paper-row expand>
            <div flex grow style="padding: 12px; gap: 24px;">
              <material-icon icon="list"></material-icon>
              <div flex column grow style="gap: 12px;">
                <div flex align-center style="height: 24px;">Overview Attributes</div>
                <div>
                  Job (metadata) attributes that appear on
                  <a class="highlighted-link" href="../pole-application-overview" target="_blank">Pole Application Overview</a>. These
                  settings help to summarize applications and make them easier to find.
                </div>
                <paper-table rounded outline grow overflow-hidden>
                  <paper-table-scroll data-path="_itemData.overview_attributes" on-sort-changed="orderedItemsArraySortChanged">
                    <!-- Header -->
                    <paper-row grey short slot="before">
                      <paper-cell icon>
                        <material-icon icon="swap_vert"></material-icon>
                      </paper-cell>
                      <paper-cell>Attribute</paper-cell>
                      <paper-cell style="max-width: 100px;" justify-center>Top Level</paper-cell>
                      <paper-cell style="max-width: 100px;" justify-center>Visibility</paper-cell>
                      <paper-cell icon>
                        <material-icon icon="unfold_more"></material-icon>
                      </paper-cell>
                      <paper-cell icon></paper-cell>
                    </paper-row>
                    <!-- Body -->
                    <template is="dom-repeat" items="[[getOrderedItemsArray(_itemData.overview_attributes.*, $true)]]" mutable-data>
                      <paper-header-content
                        data-path="_itemData.overview_attributes.$(model.item.$key)"
                        on-opened-changed="updateProperty"
                        on-render-changed="updateProperty"
                        slot="sortable"
                        opened="[[item.$opened]]"
                        divider
                        no-click-row
                      >
                        <paper-cell slot="header" icon drag-handle>[[add(index, 1)]]</paper-cell>
                        <paper-cell slot="header" input>
                          <katapult-drop-down
                            label="Attribute"
                            value="[[item.attribute]]"
                            value-path="name"
                            label-path="label"
                            items="[[_overviewColumnOptions]]"
                            no-label-float
                            data-path="_itemData.overview_attributes.$(model.item.$key).attribute"
                            on-selected-changed="updateProperty"
                          >
                          </katapult-drop-down>
                        </paper-cell>
                        <paper-cell style="max-width: 100px;" slot="header" icon>
                          <paper-checkbox
                            checked="[[item.top_level]]"
                            data-path="_itemData.overview_attributes.$(model.item.$key).top_level"
                            on-change="toggleProperty"
                          ></paper-checkbox>
                        </paper-cell>
                        <paper-cell style="max-width: 100px;" slot="header" icon>
                          <template is="dom-if" if="[[!item.limit_visibility]]">
                            <katapult-icon icon="visibility"></katapult-icon>
                          </template>
                          <template is="dom-if" if="[[item.limit_visibility]]">
                            <katapult-icon icon="visibility_lock" color="var(--paper-red-500)"></katapult-icon>
                          </template>
                        </paper-cell>
                        <paper-cell slot="header-end" icon-button>
                          <katapult-button
                            iconOnly
                            noBorder
                            icon="delete"
                            data-path="_itemData.overview_attributes.$(model.item.$key)"
                            data-value="$null"
                            on-click="updateProperty"
                          ></katapult-button>
                        </paper-cell>
                        <template is="dom-if" if="[[item.$render]]" restamp>
                          <div slot="content" flex column>
                            <!-- Top Level -->
                            <paper-row checkbox pointer>
                              <paper-cell icon>
                                <material-icon icon="label_important"></material-icon>
                              </paper-cell>
                              <paper-cell shrink>Top Level</paper-cell>
                              <paper-cell icon>
                                <paper-checkbox
                                  checked="[[item.top_level]]"
                                  data-path="_itemData.overview_attributes.$(model.item.$key).top_level"
                                  on-change="toggleProperty"
                                ></paper-checkbox>
                              </paper-cell>
                              <paper-cell italic
                                >Should this attribute always be visible or should it be displayed inside of the collapsible
                                region?</paper-cell
                              >
                            </paper-row>
                            <!-- Limit Visibility -->
                            <paper-row checkbox pointer>
                              <paper-cell icon>
                                <template is="dom-if" if="[[!item.limit_visibility]]">
                                  <katapult-icon icon="visibility"></katapult-icon>
                                </template>
                                <template is="dom-if" if="[[item.limit_visibility]]">
                                  <katapult-icon icon="visibility_lock" color="var(--paper-red-500)"></katapult-icon>
                                </template>
                              </paper-cell>
                              <paper-cell shrink>Limit Visibility</paper-cell>
                              <paper-cell icon>
                                <paper-checkbox
                                  checked="[[item.limit_visibility]]"
                                  data-path="_itemData.overview_attributes.$(model.item.$key).limit_visibility"
                                  on-change="toggleProperty"
                                ></paper-checkbox>
                              </paper-cell>
                            </paper-row>
                            <paper-row expand>
                              <paper-cell icon></paper-cell>
                              <paper-cell>
                                <paper-table white rounded outline dividers>
                                  <paper-row checkbox pointer>
                                    <paper-cell icon>
                                      <katapult-icon icon="engineering"></katapult-icon>
                                    </paper-cell>
                                    <paper-cell>Utility Company</paper-cell>
                                    <paper-cell icon>
                                      <paper-checkbox
                                        disabled="[[!item.limit_visibility]]"
                                        checked="[[item.visibility.utility_company]]"
                                        data-path="_itemData.overview_attributes.$(model.item.$key).visibility.utility_company"
                                        on-change="toggleProperty"
                                      ></paper-checkbox>
                                    </paper-cell>
                                  </paper-row>
                                  <paper-row checkbox pointer>
                                    <paper-cell icon>
                                      <katapult-icon icon="engineering"></katapult-icon>
                                    </paper-cell>
                                    <paper-cell>Review Contractors</paper-cell>
                                    <paper-cell icon>
                                      <paper-checkbox
                                        disabled="[[!item.limit_visibility]]"
                                        checked="[[item.visibility.review_contractors]]"
                                        data-path="_itemData.overview_attributes.$(model.item.$key).visibility.review_contractors"
                                        on-change="toggleProperty"
                                      ></paper-checkbox>
                                    </paper-cell>
                                  </paper-row>
                                  <paper-row checkbox pointer>
                                    <paper-cell icon>
                                      <katapult-icon icon="architecture"></katapult-icon>
                                    </paper-cell>
                                    <paper-cell>Engineering Contractors</paper-cell>
                                    <paper-cell icon>
                                      <paper-checkbox
                                        disabled="[[!item.limit_visibility]]"
                                        checked="[[item.visibility.engineering_contractors]]"
                                        data-path="_itemData.overview_attributes.$(model.item.$key).visibility.engineering_contractors"
                                        on-change="toggleProperty"
                                      ></paper-checkbox>
                                    </paper-cell>
                                  </paper-row>
                                  <paper-row checkbox pointer>
                                    <paper-cell icon>
                                      <katapult-icon icon="photo_camera"></katapult-icon>
                                    </paper-cell>
                                    <paper-cell>Data Collection Contractors</paper-cell>
                                    <paper-cell icon>
                                      <paper-checkbox
                                        disabled="[[!item.limit_visibility]]"
                                        checked="[[item.visibility.data_collection_contractors]]"
                                        data-path="_itemData.overview_attributes.$(model.item.$key).visibility.data_collection_contractors"
                                        on-change="toggleProperty"
                                      ></paper-checkbox>
                                    </paper-cell>
                                  </paper-row>
                                </paper-table>
                              </paper-cell>
                            </paper-row>

                            <!-- Label -->
                            <paper-row input>
                              <paper-cell icon>
                                <material-icon icon="label"></material-icon>
                              </paper-cell>
                              <paper-cell shrink>Label</paper-cell>
                              <paper-cell>
                                <paper-input
                                  label="Label"
                                  value="[[item.label]]"
                                  data-path="_itemData.overview_attributes.$(model.item.$key).label"
                                  underline
                                  no-label-float
                                  on-value-changed="updateProperty"
                                ></paper-input>
                              </paper-cell>
                            </paper-row>
                            <!-- Display As -->
                            <paper-row input>
                              <paper-cell icon>
                                <material-icon icon="list"></material-icon>
                              </paper-cell>
                              <paper-cell shrink>Display As</paper-cell>
                              <paper-cell>
                                <katapult-drop-down
                                  label="Select a Display Type"
                                  value="[[item.display_as]]"
                                  label-path="."
                                  value-path="."
                                  label-function="[[camelCase]]"
                                  items="[[displayAsOptions]]"
                                  no-label-float
                                  data-path="_itemData.overview_attributes.$(model.item.$key).display_as"
                                  on-selected-changed="updateProperty"
                                >
                                </katapult-drop-down>
                              </paper-cell>
                            </paper-row>
                          </div>
                        </template>
                      </paper-header-content>
                    </template>
                    <!-- Add Button -->
                    <paper-row
                      class="addButton"
                      light-grey
                      short
                      slot="after"
                      data-path="_itemData.overview_attributes"
                      on-click="addOrderedItem"
                    >
                      <paper-cell>
                        <paper-ripple></paper-ripple>
                        <material-icon icon="playlist_add"></material-icon>
                        <div>Add an Application Attribute</div>
                      </paper-cell>
                    </paper-row>
                  </paper-table-scroll>
                </paper-table>
              </div>
            </div>
          </paper-row>
          <paper-row expand>
            <div flex grow style="padding: 12px; gap: 24px;">
              <katapult-icon icon="signature"></katapult-icon>
              <div flex column grow style="gap: 12px;">
                <div flex align-center style="height: 24px;">Agreements</div>
                <div>
                  Agreements are forms that applicants must sign before advancing their application. To use an agreement in your
                  application, select "Prompt Agreement" as the primary action of an action button.
                </div>
                <paper-table rounded white outline grow overflow-hidden>
                  <!-- Header -->
                  <paper-row grey short>
                    <paper-cell>Agreement Name</paper-cell>
                    <paper-cell icon></paper-cell>
                  </paper-row>
                  <!-- Body -->
                  <template is="dom-repeat" items="[[getItemsArray(_itemData.agreements.*)]]" mutable-data>
                    <paper-header-content
                      data-path="_itemData.agreements.$(model.item.$key)"
                      on-opened-changed="updateProperty"
                      on-render-changed="updateProperty"
                      opened="[[item.$opened]]"
                      divider
                      no-click-row
                    >
                      <paper-cell slot="header" input>
                        <paper-input
                          label="Name"
                          value="[[item.name]]"
                          data-path="_itemData.agreements.$(model.item.$key).name"
                          no-label-float
                          on-value-changed="updateProperty"
                        ></paper-input>
                      </paper-cell>
                      <paper-cell slot="header-end" icon-button>
                        <katapult-button
                          iconOnly
                          noBorder
                          icon="delete"
                          data-path="_itemData.agreements.$(model.item.$key)"
                          data-value="$null"
                          on-click="updateProperty"
                        ></katapult-button>
                      </paper-cell>
                      <template is="dom-if" if="[[item.$render]]" restamp>
                        <div class="settings-table" slot="content" flex column>
                          <paper-row>
                            <div
                              style="background-color: var(--paper-orange-400); padding: 12px; border-radius: 12px; box-sizing: border-box;"
                            >
                              NOTE: There are additional agreement configurations that are not currently accessible here. Contact
                              <a href="mailto:support@katapultengineering.com">support@katapultengineering.com</a> if you need to make
                              changes to the inputs or content of this agreement, or if you are setting up a new agreement.
                            </div>
                          </paper-row>
                        </div>
                      </template>
                    </paper-header-content>
                  </template>
                  <!-- Add Button -->
                  <paper-row class="addButton" light-grey short data-path="_itemData.agreements" on-click="addItem">
                    <paper-cell>
                      <paper-ripple></paper-ripple>
                      <katapult-icon icon="add_circle_outline"></katapult-icon>
                      <div>Add an Agreement</div>
                    </paper-cell>
                  </paper-row>
                </paper-table>
              </div>
            </div>
          </paper-row>
        </paper-table>
      `;
    }

    static get properties() {
      return {
        displayAsOptions: {
          type: Array,
          value: () => ['boolean', 'date', 'company', 'computed']
        }
      };
    }

    static get observers() {
      return ['_updateOverviewColumnOptions(otherAttributes.*)'];
    }

    _updateOverviewColumnOptions() {
      this._overviewColumnOptions = getColumnOptions(this.otherAttributes);
    }

    async beforeSave() {
      const cancelSaveProcess = await confirmSyncPortalConfig(this.modelSpaceDetails.$key);
      if (cancelSaveProcess) throw new Error('User did not confirm sync portal config');
    }

    async afterSave() {
      await syncPortalConfigs(this.modelSpaceDetails.$key);
    }
  }
);
